<template>
  <div>
    <b-sidebar
      id="sidebar-add-new-event"
      sidebar-class="sidebar-lg"
      :visible="isEventHandlerSidebarActive"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      width="50%"
      @hidden="onHide"
      @shown="onShown"
      @change="(val) => $emit('show-hide-sidebar', val)"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ eventLocal.id ? $t('shifts_calendar.update') : $t('shifts_calendar.add') }} {{ $t('shifts_calendar.shift').toLowerCase() }}
          </h5>
          <div>
            <feather-icon
              v-if="eventLocal.id"
              v-b-modal.modal-delete
              icon="TrashIcon"
              class="cursor-pointer"
            />
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>

        <!-- Body -->
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(safeSubmit)"
            @reset.prevent="resetForm"
          >
            <!-- Form Actions -->
            <div
              class="d-flex mb-2"
              style="justify-content: flex-end;"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                type="submit"
                :disabled="isLoading || isDoubleBooking || isPartialBlocked || isContractExpired"
              >
                {{ eventLocal.id ? $t('shifts_calendar.update') : $t('shifts_calendar.add') }}
              </b-button>
            </div>

            <b-row>
              <!-- section info -->
              <b-col
                v-if="eventLocal.extendedProps.calendar !== 'Draft'  && eventLocal.extendedProps.calendar !== 'Open'"
                style="margin-bottom: -5px;"
                cols="12">
                <b-form-group
                  :label="$t('gig_page.local_service_area')"
                  label-for="section"
                >
                  <vue-autosuggest
                    v-model="areaName"
                    :suggestions="filteredLocationOptions"
                    :get-suggestion-value="getLocationSuggestionValue"
                    :limit="10"
                    :input-props="{
                      id: 'autosuggest__input',
                      class: 'form-control',
                      placeholder: $t('gig_page.location_placeholder'),
                      style: 'text-transform: capitalize',
                    }"
                    @selected="onLocationSelected"
                    @input="onLocationInputChange"
                  >
                    <template slot-scope="{ suggestion }">
                      <span
                        class="my-suggestion-item"
                        style="text-transform: capitalize"
                      >
                        {{ suggestion.item.name }}
                      </span><br>
                      <small class="my-suggestion-item">{{ suggestion.item.location.address.streetAddress }} - {{ suggestion.item.location.address.city }}</small>
                    </template>
                  </vue-autosuggest>
                </b-form-group>
                <b-form-invalid-feedback
                  v-if="isContractExpired"
                  class="my-1"
                  :state="false"
                >
                  Alueen sopimus on vanhentunut, päivitä sopimus jotta voit julkaista vuoroja!
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <hr v-if="appConfig.type === 'ow-admin'">

            <!-- Start -->
            <validation-provider
              #default="validationContext"
              name="Start"
              rules="required"
            >

              <b-form-group
                :label="$t('gig_page.start')"
                label-for="start"
                :state="getValidationState(validationContext)"
              >
                <flat-pickr
                  v-model="eventLocal.startStr"
                  class="form-control"
                  :config="{ enableTime: true, time_24hr: true }"
                  @on-change="(date) => { setDate(eventLocal, date, 'start') }"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- End Date -->
            <validation-provider
              #default="validationContext"
              name="End"
              rules="required"
            >

              <b-form-group
                :label="$t('gig_page.end')"
                label-for="end"
                :state="getValidationState(validationContext)"
                style="margin-bottom: -5px;"
              >
                <flat-pickr
                  v-model="eventLocal.endStr"
                  class="form-control"
                  :config="{ enableTime: true, minDate: eventLocal.start, time_24hr: true }"
                  @on-change="(date) => { setDate(eventLocal, date, 'end') }"
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <!-- Assigned to -->
            <b-form-group
              v-if="user.role=='customer' && appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.assigned_user')"
              label-for="assigned-to"
            >
              <div v-if="assignedUser">
                <p
                  style="text-transform: capitalize"
                >
                  {{ $t('shifts_calendar.fullName') }}: {{ assignedUser.fullName }}
                </p>
                <p
                  style="text-transform: capitalize"
                >
                  {{ $t('shifts_calendar.role') }}: {{ assignedUser.role }}, {{ assignedUser.love ? $t('action_center.permissible') : $t('action_center.unauthorized') }}
                </p>
                <p>
                  {{ $t('shifts_calendar.mobile') }}: {{ assignedUser.mobile }}
                </p>
                <p>
                  {{ $t('shifts_calendar.email') }} {{ assignedUser.email }}
                </p>
                <p>{{ `${$t('birthday')}: ${assignedUser.birthday}` }}</p>
              </div>
            </b-form-group>
            <b-row
              v-if="!eventLocal.extendedProps.boosted"
              class="mx-0 my-1"
            >
              <b-col cols="2.5">
                <b-form-checkbox
                  v-if="user.role=='admin'"
                  name="check-button"
                  @change="onSelectedBoosted"
                >
                  {{ $t('boost_option_title') }}
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="auto"
                style="min-width: 215px;"
              >
                <b-form-group
                  v-if="isBoosted"
                  :label="$t('boost_type_label')"
                  label-for="boost_type"
                  style="margin-left: 15px"
                >
                  <v-select
                    id="boost_type"
                    v-model="selectedBoostedOption"
                    :options="boostedOptions"
                    :clearable="false"
                    inline
                    @input="onSelectedBoostType(selectedBoostedOption)"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="auto">
                <b-form-group
                  v-if="isBoosted && selectedBoostedOption.value !== 'quick'"
                  :label="$t('boost_option_title')"
                  label-for="boost_value"
                  style="margin-left: 10px"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('boost_option_title')"
                    rules="required"
                  >
                    <b-form-input
                      id="boost_value"
                      v-model="selectedBoostedValue"
                      type="number"
                      :placeholder="$t('boost_value_placeholder')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <hr v-if="appConfig.type === 'ow-admin'">

            <b-row>
              <b-col cols="12">
                <b-form-group
                  v-if="user.role=='admin' || appConfig.type === 'ow-admin'"
                  :label="$t('shifts_calendar.assigned_to')"
                  label-for="assigned-to"
                >
                  <vue-autosuggest
                    v-model="assignedTo"
                    :suggestions="filteredOptions"
                    :input-props="inputProps"
                    :get-suggestion-value="getSuggestionValue"
                    :limit="10"
                    @selected="onSelected"
                    @input="onInputChange"
                  >
                    <template slot-scope="{suggestion}">
                      <div class="d-flex align-items-center">
                        <b-avatar
                          :text="avatarText(suggestion.item.fullName)"
                          variant="light-success"
                          :src="suggestion.item.avatar"
                        />
                        <div class="detail ml-50">
                          <b-card-text
                            class="mb-0"
                            style="text-transform: capitalize"
                          >
                            {{ suggestion.item.fullName }}
                          </b-card-text>
                        </div>
                      </div>
                    </template>
                  </vue-autosuggest>
                </b-form-group>
              </b-col>
            </b-row>

            <div
              v-if="isLoading"
              class="m-1 d-flex align-items-center"
            >
              <b-spinner small />
              <span style="margin-left:5px;">{{ $t('loading') }}</span>
            </div>

            <b-form-invalid-feedback
              v-if="isDoubleBooking"
              class="my-1"
              :state="false"
            >
              {{ $t('shifts_calendar.double_shift_warning') }}
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-if="isPartialBlocked"
              class="my-1"
              :state="false"
            >
              {{ $t('shifts_calendar.block_modal.blocked_in_area_or_account') }}
            </b-form-invalid-feedback>

            <b-button
              v-if="eventLocal.extendedProps.assignedTo && !isPartialBlocked && appConfig.type === 'hw-admin'"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-b-modal.block-partial-modal
              variant="outline-danger"
              type="button"
              class="mb-1"
            >
              {{ $t('shifts_calendar.block_modal.block_btn') }}
            </b-button>
            <div
              v-if="eventLocal.extendedProps.calendar === 'Draft'"
              class="d-flex mb-2"
              style="justify-content: flex-start;"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                v-b-modal.publish-shifts-modal
                variant="primary"
                type="button"
              >
                {{ $t('shifts_calendar.publish_shifts.btn_text') }}
              </b-button>
            </div>
            <div
              v-if="assignedUser && assignedUser.firstName && eventLocal.end >= new Date() && finalState !== 'Expired'"
              class="d-flex mb-2"
              style="justify-content: flex-end;"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                v-b-modal.modal-warning
                variant="info"
                type="button"
              >
                {{ $t('shifts_calendar.cancel_options.btnText') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                v-b-modal.modal-danger
                variant="danger"
                type="button"
                class="ml-1"
              >
                {{ $t('shifts_calendar.buttons.cancel_button') }}
              </b-button>
            </div>

            <b-row>
              <b-col cols="12">
                <b-form-group
                  v-if="new Date() > eventLocal.end && eventLocal.extendedProps.calendar !== 'Open'"
                  :label="$t('shifts_calendar.confirm_shift')"
                  label-for="state"
                >
                  <v-select
                    v-model="finalState"
                    :options="statusOptions"
                    :clearable="false"
                    style="text-transform: capitalize"
                    @input="onUpdateConfirmed(finalState.value)"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <app-collapse
              class="w-full mb-2"
            >
              <app-collapse-item
                :title="$t('show_more')"
              >
                <b-form-group
                  v-if="eventLocal.extendedProps.requirements && appConfig.type === 'ow-admin'"
                  :label="$t('shifts_calendar.requirements')"
                  label-for="requirements"
                >
                  <b-form-input
                    id="requirements"
                    style="text-transform: uppercase"
                    :value="eventLocal.extendedProps.requirements ? eventLocal.extendedProps.requirements.join(', ') : ''"
                    plaintext
                    trim
                  />
                </b-form-group>
                <!-- Billing Status Options -->
                <b-form-group
                  :label="$t('shifts_calendar.workday_type')"
                  label-for="workday-type"
                >
                  <v-select
                    v-model="eventLocal.extendedProps.workDayType"
                    :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="workday-type"
                    @input="onUpdateShift(eventLocal.extendedProps.workDayType, 'workDayType')"
                  />
                </b-form-group>

                <!--- Kilometer input -->
                <b-form-group
                  :label="$t('kilometer_allowance')"
                  label-for="kilometrikorvaus"
                >
                  <b-form-input
                    id="kilometrikorvaus"
                    v-model="eventLocal.extendedProps.kilometrikorvaus"
                    type="number"
                    step="0.1"
                    min="0"
                    @change="onUpdateShift(eventLocal.extendedProps.kilometrikorvaus, 'kilometrikorvaus')"
                  />
                  <!-- @input="handleInputKilometer($event)" -->
                </b-form-group>
              </app-collapse-item>
            </app-collapse>

            <!--- Confirm to location -->
            <!-- <b-form-group
              v-if="(new Date() < eventLocal.start) && assignedUser && assignedTo && user.role=='admin'"
              :label="$t('shifts_calendar.verify_booking')"
              label-for="state"
            >
              <b-form-checkbox
                v-model="eventLocal.extendedProps.reserved"
                name="check-button"
                style="margin-bottom: 10px"
                switch
                inline
                @change="onVerifiedToggle(eventLocal.extendedProps.reserved)"
              >
                {{ $t('shifts_calendar.booking_verified') }}
              </b-form-checkbox>
              <div v-if="!eventLocal.extendedProps.reserved">
                <b-form-group
                  :label="$t('shifts_calendar.email')"
                  label-for="email"
                  label-cols-md="3"
                >
                  <v-select
                    v-model="emailsTo"
                    multiple
                    taggable
                    :no-drop="true"
                    style="text-transform: lowercase"
                  />
                  <small
                    v-if="!emailsTo.length"
                    class="text-danger"
                  >
                    {{ $t('shifts_calendar.client_email_warning') }}
                  </small>
                </b-form-group>
                <b-form-group
                  :label="$t('shifts_calendar.kohde')"
                  label-for="kohde"
                  label-cols-md="3"
                >
                  <b-form-input
                    id="kohde"
                    v-model="verificationSection"
                    trim
                  />
                </b-form-group>
                <b-form-group
                  :label="$t('shifts_calendar.puh')"
                  label-for="puh"
                  label-cols-md="3"
                >
                  <b-form-input
                    id="puh"
                    v-model="verificationMobile"
                    trim
                  />
                </b-form-group>
                <b-form-group
                  :label="$t('shifts_calendar.hetu')"
                  label-for="hetu"
                  label-cols-md="3"
                >
                  <b-form-input
                    id="hetu"
                    v-model="verificationHetu"
                    trim
                  />
                </b-form-group>
                <b-button
                  v-if="!sending"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  :disabled="!emailsTo.length"
                  variant="primary"
                  class="mr-2"
                  style="margin-top: 10px"
                  @click="sendVerification"
                >
                  {{ $t('shifts_calendar.send_verification_email') }}
                </b-button>
                <b-button
                  v-if="sending"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-2"
                  style="margin-top: 10px"
                  disabled
                >
                  <b-spinner small />
                  {{ $t('shifts_calendar.sending') }}
                </b-button>
              </div>
            </b-form-group> -->
            <!--- Set final state -->

            <!-- Requirements -->
            <b-form-group
              v-if="eventLocal.extendedProps.requirements && appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.requirements')"
              label-for="requirements"
            >
              <b-form-input
                id="requirements"
                style="text-transform: uppercase"
                :value="eventLocal.extendedProps.requirements ? eventLocal.extendedProps.requirements.join(', ') : ''"
                plaintext
                trim
              />
            </b-form-group>

            <!-- Boosted -->
            <b-form-group
              v-if="eventLocal.extendedProps.boosted && appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.shift_boosted_title')"
              label-for="shift_boosted"
            >
              <div class="d-flex align-items-center">
                <b-form-input
                  id="shift_boosted"
                  style="text-transform: uppercase; width: auto;"
                  :value="(eventLocal.extendedProps.boostedOptions.boostType !== 'quick') ?
                    (eventLocal.extendedProps.boostedOptions.boostType + ': ' + eventLocal.extendedProps.boostedOptions.value) :
                    (eventLocal.extendedProps.boostedOptions.boostType)"
                  plaintext
                  trim
                />
                <!--
                <b-button
                  v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                  size="sm"
                  variant="outline-danger"
                  @click="deleteBoosted"
                >
                  <feather-icon
                    icon="XIcon"
                    size="18"
                  />
                </b-button>-->
              </div>
            </b-form-group>
            <!-- customer -->
            <b-form-group
              v-if="appConfig.type === 'hw-admin'"
              :label="`${$t('account')}:`"
              label-for="account"
              label-cols-md="3"
            >
              <b-form-input
                id="account"
                v-model="accountName"
                plaintext
                trim
              />
            </b-form-group>
            <!-- section -->
            <b-form-group
              v-if="appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.section')"
              label-for="section"
              label-cols-md="3"
            >
              <b-form-input
                id="section"
                v-model="sectionInfo"
                plaintext
                trim
              />
            </b-form-group>
            <!-- location -->
            <b-form-group
              v-if="appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.location')"
              label-for="event-location"
              label-cols-md="3"
            >
              <b-form-input
                id="location"
                v-model="location"
                plaintext
                trim
                :placeholder="$t('shifts_calendar.location')"
              />
            </b-form-group>

            <!-- location details -->
            <b-form-group
              v-if="appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.details')"
              label-for="details"
              label-cols-md="3"
            >
              <b-form-textarea
                id="details"
                v-model="locationDetails"
                plaintext
                no-resize
                rows="3"
              />
            </b-form-group>

            <!-- abscence -->
            <b-form-group
              v-if="appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.abscence')"
              label-for="abscence"
              label-cols-md="3"
            >
              <b-form-textarea
                id="abscence"
                v-model="absenceInfo"
                plaintext
                no-resize
                rows="3"
              />
            </b-form-group>

            <!-- contact -->
            <b-form-group
              v-if="appConfig.type === 'hw-admin'"
              :label="$t('shifts_calendar.contact')"
              label-for="contact"
              label-cols-md="3"
            >
              <b-form-input
                v-for="(item, index) in contacts"
                :key="index"
                :value="item"
                plaintext
                trim
              />
            </b-form-group>
            <!-- updates -->
            <app-collapse>
              <app-collapse-item
                :title="$t('shifts_calendar.updates')"
              >
                <calendar-event-updates :updates="updates" />
                <!-- <b-card-text
                  v-for="(item, index) in updates"
                  :key="index"
                >
                  {{ item.state }} - {{ item.processer }} - {{ item.date }}
                  <p
                    v-for="(value, i) in item.content"
                    :key="i"
                  > {{ value }} </p>
                </b-card-text> -->
              </app-collapse-item>
            </app-collapse>
          </b-form>
        </validation-observer>
        <b-modal
          id="modal-danger"
          ok-only
          ok-variant="danger"
          :ok-title="$t('shifts_calendar.buttons.cancel_button')"
          modal-class="modal-danger"
          centered
          :title="$t('shifts_calendar.buttons.cancel_button')"
          :ok-disabled="!formCancel.mobile || !formCancel.email"
          @ok="onCancelShift"
          @show="onShowCancelShift"
          @hidden="onHiddenCancelShift"
        >
          <b-card-text class="mt-2">
            {{ $t('shifts_calendar.cancel_form.title') }}
          </b-card-text>
          <b-input-group prepend="FI (+358)">
            <b-form-input
              v-model="formCancel.mobile"
              type="number"
              :placeholder="$t('shifts_calendar.cancel_form.mobile_placeholder')"
            />
          </b-input-group>

          <b-form-input
            v-model="formCancel.email"
            type="email"
            :placeholder="$t('shifts_calendar.cancel_form.email_placeholder')"
            class="mt-2"
          />
        </b-modal>
        <b-modal
          id="modal-warning"
          ok-only
          ok-variant="info"
          :ok-title="$t('shifts_calendar.cancel_options.confirmBtnText')"
          centered
          :title="$t('shifts_calendar.cancel_options.title')"
          :ok-disabled="!newFormData.mobile || !newFormData.email"
          @ok="openShift"
          @show="onShowCancelShift"
          @hidden="onHiddenCancelShift"
        >
          <b-form-group>
            <v-select
              v-model="newFormData.titleSelected"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="titleOptions"
              :reduce="val => val.value"
              :clearable="false"
            />
          </b-form-group>
          <b-form-group v-if="newFormData.titleSelected === 'option_1'">
            <p>{{ $t('message')}}:</p>
            <p>{{ getMessage.smsMessage }}</p>
          </b-form-group>
          <b-form-group v-else-if="newFormData.titleSelected === 'option_2'">
            <p>{{ $t('message')}}:</p>
            <p>{{ getMessage.smsMessage }}</p>
          </b-form-group>
          <b-form-group v-else-if="newFormData.titleSelected === 'option_3'">
            <p>{{ $t('message')}}:</p>
            <b-form-textarea
              v-model.trim="newFormData.textAreaFormData"
              no-resize
              rows="5"
              maxlength="200"
            />
            <p
              class="mt-1"
              style="text-align:right;"
            >
              {{ newFormData.textAreaFormData.length }} / 200
            </p>
          </b-form-group>
          <b-input-group prepend="FI (+358)">
            <b-form-input
              v-model="newFormData.mobile"
              type="number"
              :placeholder="$t('shifts_calendar.cancel_form.mobile_placeholder')"
            />
          </b-input-group>

          <b-form-input
            v-model="newFormData.email"
            type="email"
            :placeholder="$t('shifts_calendar.cancel_form.email_placeholder')"
            class="mt-2"
          />
          <b-form-checkbox v-model="employeeCancelled" style="margin-top: 10px;">
            {{ $t('user_cancelled_shift') }}
          </b-form-checkbox>
        </b-modal>
        <b-modal
          id="modal-delete"
          ok-only
          :ok-variant="canDelete ? 'danger' : ''"
          :ok-title="canDelete ? $t('delete') : $t('close')"
          :modal-class="canDelete ? 'modal-danger' : 'modal-warning'"
          centered
          :title="canDelete ? $t('shifts_calendar.delete_modals.title_delete_shift') : $t('shifts_calendar.delete_modals.warning_title_delete_shift')"
          @ok="canDelete ? $emit('remove-event',eventLocal.id, processer) && hide() : null"
        >
          <b-card-text>
            {{ canDelete ? $t('shifts_calendar.delete_modals.content_delete_shift') : $t('shifts_calendar.delete_modals.content_cannot_delete_shift') }}
          </b-card-text>
        </b-modal>
        <b-modal
          id="modal-delete-boosted"
          ok-only
          ok-variant="danger"
          :ok-title="$t('delete')"
          modal-class="modal-danger"
          centered
          :title="$t('shifts_calendar.delete_modals.warning_title_delete_shift')"
          @ok="deleteBoosted"
        >
          <b-card-text>
            {{ $t('shifts_calendar.delete_modals.warning_boost_remove') }}
          </b-card-text>
        </b-modal>

        <b-modal
          id="publish-shifts-modal"
          :title="$t('shifts_calendar.publish_shifts.title')"
          hide-footer
        >
          <b-card-text>
            {{ $t('shifts_calendar.publish_shifts.text') }}
          </b-card-text>
          <b-button class="mt-3" variant="primary" block @click="publishShifts(true)">{{ $t('shifts_calendar.publish_shifts.publish_all_btn') }}</b-button>
          <b-button class="mt-2" variant="outline-primary" block @click="publishShifts(false)">{{ $t('shifts_calendar.publish_shifts.publish_one_btn') }}</b-button>
        </b-modal>

        <b-modal
          id="block-partial-modal"
          centered
          :title="$t('shifts_calendar.block_modal.title')"
          :ok-title="$t('shifts_calendar.block_modal.block_btn')"
          :cancel-title="$t('cancel')"
          ok-variant="danger"
          modal-class="modal-danger"
          no-close-on-backdrop
          :ok-disabled="!blockReason"
          @ok="handlePartialBlock"
          @hide="resetPartialBlockForm"
        >
          <b-form-group
            :label="$t('block_from')"
            label-for="block-type"
          >
            <v-select
              v-model="blockType"
              :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
              :options="blockTypeOptions"
              :reduce="val => val.value"
              :clearable="false"
              class="capitalized"
            />
          </b-form-group>
          <b-form-group>
            <b-form-input
              :value="blockType === 'account' ? selectedAccount.value.name : sectionInfo"
              class="capitalized"
              disabled
            />
          </b-form-group>
          <b-form-group
            :label="$t('block_reason')"
            label-for="block-reason"
          >
            <b-form-textarea
              id="block-reason"
              v-model="blockReason"
              rows="4"
              max-rows="4"
              :placeholder="$t('shifts_calendar.block_modal.block_reason_placeholder')"
            />
            <small
              v-if="!blockReason"
              class="text-danger"
            >{{ $t('shifts_calendar.block_modal.block_reason_warning') }}</small>
          </b-form-group>
        </b-modal>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BAvatar, BFormTextarea, BButton, BFormInvalidFeedback,
  BCardText, BFormCheckbox, BSpinner, BInputGroup, BRow, BCol,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email, url } from '@validations'
import formValidation from '@hw-ui/comp-functions/forms/form-validation'
import { ref, toRefs } from '@vue/composition-api'
import { avatarText } from '@hw-ui/utils/filter'
import i18n from '@hw-core/libs/i18n'
import AppCollapse from '@hw-ui/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@hw-ui/components/app-collapse/AppCollapseItem.vue'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import _ from 'lodash' // TODO no need
import { capitalize } from '@hw-core/utils'
import useCalendarEventHandler from './useCalendarEventHandler'
import CalendarEventUpdates from './CalendarEventUpdates.vue'

function timeConflict(time, start, end) {
  if (start <= time && time <= end) {
    return true
  }
  return false
}

function toHTMLMessage(message) {
  const array = message.split('\n')
  const htmlArray = array.map(elem => `<p>${elem}</p>`)
  return htmlArray.join('')
}
export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BInputGroup,
    BFormCheckbox,
    BFormInput,
    BFormTextarea,
    BAvatar,
    BSpinner,
    BCardText,
    flatPickr,
    ValidationProvider,
    BFormInvalidFeedback,
    ValidationObserver,
    VueAutosuggest,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    CalendarEventUpdates,
    BRow,
    BCol,
  },
  directives: {
    Ripple,
  },
  props: {
    isEventHandlerSidebarActive: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      required,
      email,
      url,
      isContractExpired: false,
      location: '',
      emailsTo: [],
      verificationHetu: '',
      verificationSection: '',
      verificationMobile: '',
      finalState: null,
      sending: false,
      employeeCancelled: false,
      locationDetails: '',
      sectionInfo: '',
      accountName: '',
      absenceInfo: '',
      contacts: [],
      filteredOptions: [],
      filteredLocationOptions: [],
      selected: '',
      assignedTo: '',
      previousAssignedTo: '',
      assignedUser: null,
      query: '',
      inputProps: {
        id: 'autosuggest__input',
        class: 'form-control',
        style: 'text-transform: capitalize',
        placeholder: this.$t('shifts_calendar.assign_to_placeholder'),
      },
      kilometrikorvaus: 0,
      typeOptions: [
        { label: this.$t('shifts_calendar.sick_day'), value: 'sick' },
        { label: this.$t('shifts_calendar.sick_day_pending'), value: 'sick_document_pending' },
        { label: this.$t('shifts_calendar.temp_nursing_leave'), value: 'temp_nursing_leave' },
        { label: this.$t('shifts_calendar.temp_nursing_leave_document_pending'), value: 'temp_nursing_leave_document_pending' },
        { label: this.$t('shifts_calendar.work_day'), value: 'work' },
        { label: this.$t('shifts_calendar.orientation_day'), value: 'orientation' },
        { label: this.$t('shifts_calendar.absence'), value: 'absence' },
      ],
      statusOptions: [
        { label: i18n.t('unconfirmed'), value: 'booked' },
        { label: i18n.t('confirmed'), value: 'confirmed' },
        { label: i18n.t('expired'), value: 'expired' },
      ],
      workDayType: '',
      processer: {},
      updates: [],
      changes: {
        processer: '',
        content: [],
      },
      countSetDate: 0,
      isOpeningForm: false,
      formCancel: {
        email: '',
        mobile: '',
      },
      titleOptions: [
        { label: this.$t('shifts_calendar.cancel_options.option_1', { msg: 'Option 1' }), value: 'option_1' },
        { label: this.$t('shifts_calendar.cancel_options.option_2', { msg: 'Option 2' }), value: 'option_2' },
        { label: this.$t('shifts_calendar.cancel_options.option_3', { msg: 'Option 3' }), value: 'option_3' },
      ],
      newFormData: {
        titleSelected: 'option_1',
        textAreaFormData: '',
        email: '',
        mobile: '',
      },
      option1Message: '',
      option1EmailMessage: '',
      option2Message: '',
      option2EmailMessage: '',
      isLoading: false,
      isDoubleBooking: false,
      isBoosted: false,
      selectedBoostedOption: { label: i18n.t('quick'), value: 'quick' },
      boostedOptions: [
        { label: i18n.t('quick'), value: 'quick' },
        { label: i18n.t('bonus'), value: 'bonus' },
      ],
      selectedBoostedValue: '',
      isPartialBlocked: false,
      areaId: '',
      areaName: '',
      contractId: null,
      blockType: 'account',
      blockTypeOptions: [
        { label: 'Kaikki tämän alueen kohteet', value: 'account' },
        { label: 'Yksittäinen alue', value: 'area' },
      ],
      blockReason: '',
    }
  },
  computed: {
    canDelete() {
      return this.eventLocal.extendedProps.calendar === 'Open' || this.eventLocal.extendedProps.calendar === 'Draft'
    },
    user() {
      return this.$store.state.appAuth.authUser ? this.$store.state.appAuth.authUser : {}
    },
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
    getMessage() {
      let emailMessage = ''
      let smsMessage = ''
      if (this.eventLocal) {
        const name = this.assignedUser ? capitalize(this.assignedUser.firstName) : ''
        const start = this.eventLocal.start?.toLocaleString('fi', {
          day: 'numeric', month: 'numeric', hour: 'numeric', minute: 'numeric',
        })
        const end = this.eventLocal.end?.toLocaleString('fi', { hour: 'numeric', minute: 'numeric' })
        const shiftTitle = this.verificationSection ? capitalize(this.verificationSection) : ''
        if (this.newFormData.titleSelected === 'option_1') {
          emailMessage = `<p>Hei ${name},</p>
<p>emme voi vahvistaa vuoroa ${shiftTitle} ajalla ${start} - ${end} sillä valitettavasti vuoro vaati perehdytyksen. Pahoittelut tästä.</p><p>Yst.Terveisin - Hoiwa tiimi</p>`
          smsMessage = `Hei  ${name},
emme voi vahvistaa vuoroa ${shiftTitle} ajalla ${start} - ${end} sillä valitettavasti vuoro vaati perehdytyksen. Pahoittelut tästä. Yst.Terveisin - Hoiwa tiimi`
        } else if (this.newFormData.titleSelected === 'option_2') {
          emailMessage = `<p>Hei ${name},</p>
<p>emme voi vahvistaa vuoroa ${shiftTitle} ajalla ${start} - ${end} sillä valitettavasti et täytä kaikkia vuoron vaatimuksia. Pahoittelut tästä.</p><p>Yst.Terveisin - Hoiwa tiimi</p>`
          smsMessage = `Hei ${name},
emme voi vahvistaa vuoroa ${shiftTitle} ajalla ${start} - ${end} sillä valitettavasti et täytä kaikkia vuoron vaatimuksia. Pahoittelut tästä. Yst.Terveisin - Hoiwa tiimi`
        }
      }
      return { smsMessage, emailMessage }
    },
    selectedAccount() {
      return this.$store.state.appAccount.selectedAccount
    },
  },
  watch: {
    assignedTo(newVal) {
      if (newVal.length === 0) {
        this.eventLocal.extendedProps.assignedTo = ''
        this.eventLocal.title = this.sectionInfo
        if (this.isOpeningForm === true) {
          this.changes = {
            state: this.changes.state ? this.changes.state : 'booked',
            content: { ...this.changes.content, booked: 'Empty' },
          }
        }
      }
    },
    selectedBoostedValue(newVal) {
      if (this.selectedBoostedOption.value === 'bonus') {
        this.changes.content.boosted = `bonus : ${newVal}`
      }
    },
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          firstName: user.firstName,
          lastName: user.lastName,
        }
      })
  },
  methods: {
    async onLocationSelected(option) {
      if (option) {
        this.areaId = option.item.id
        this.absenceInfo = option.item.location.absenceInfo
        this.location = `${option.item.location.address.streetAddress}, ${option.item.location.address.city}`
        this.locationDetails = option.item.location.locationDetails
        this.sectionInfo = option.item.location.sectionInfo
        this.absenceInfo = option.item.location.abscenceInfo
        this.contacts = option.item.location.contact
        this.eventLocal.title = this.assignedUser.id === '' ? this.sectionInfo : `${this.assignedUser.firstName}:${this.sectionInfo}`
        this.contractId = option.item.contract
        this.isContractExpired = await this.$store.dispatch('appAuth/isContractExpired', { contractId: option.item.contract })
        this.changes = {
          state: 'updated',
          content: ['updated shift area']
        }
      }
    },
    onLocationInputChange(text) {
      if (text === '' || text === undefined) {
        this.isContractExpired = false
        return
      }
      this.$emit('search-location', {
        text: text.toLowerCase(),
        accountId: this.eventLocal.extendedProps.accountId,
        callback: result => {
          this.filteredLocationOptions = [{ data: result }]
        },
      })
    },
    getLocationSuggestionValue(option) {
      return option.item.name
    },
    publishShifts(all) {
      this.$emit('publish-shifts', {
        publishAll: all,
        shift: this.eventLocal,
        callback: () => {
          this.$bvModal.hide('publish-shifts-modal')
          this.$emit('show-hide-sidebar', false)
        },
      }
      )
    },
    setDate(item, date, slot) {
      if (this.eventLocal.startStr && this.eventLocal.endStr) {
        this.countSetDate += 1
        if (this.countSetDate > 2) this.onUpdateShift(this.onConvertDate(date), slot)
      }
      /* eslint-disable no-param-reassign */
      if (slot === 'start') {
        [item.start] = date
      } else {
        [item.end] = date
      }
    },
    sendVerification() {
      /* eslint-disable quotes */
      /* eslint-disable prefer-template */
      const { role, fullName } = this.assignedUser
      const mobile = this.verificationMobile
      const love = this.assignedUser.love ? 'luvallinen' : 'luvaton'
      const start = this.eventLocal.start.toLocaleString('fi')
      const end = this.eventLocal.end.toLocaleString('fi')
      const subject = 'Vuoro otettu'
      const shiftTitle = this.verificationSection
      const hetu = this.verificationHetu ? this.verificationHetu : 'Pitää kysyä/Annettu'
      this.sending = true
      const message = `<p>Hei,</p>
                  <p>Vahvistamme meidän puolelta että seuraava vuoro on varattu:</p>
                  <p style="text-transform:capitalize"><b>Varaaja: </b>  ` + fullName + ` ` + role + ` ` + love + ` Puh:` + mobile + `</p>
                  <p><b>HETU:  ` + hetu + `</p>
                  <p><b>Paikka:  ` + shiftTitle + `</p>
                  <p><b>Alkaa: </b> ` + start + `</p>
                  <p><b>Loppuu: </b> ` + end + `</p>
                  <p>Mikäli tarvitsette lisää tietoa voitte laittaa viestin osoitteeseen
                  <b>tilaus@hoiwa.fi</b><p>
                  <p>Ystävällisin terveisin,</p>
                  <p>Hoiwa ‑tiimi</p>
              `
      this.emailsTo.forEach(item => {
        this.$emit('send-verification', {
          to: item,
          message,
          subject,
          callback: () => {
            this.eventLocal.extendedProps.reserved = true
            this.sending = false
            this.onVerifiedToggle(true, this.emailsTo)
            this.safeSubmit()
          },
        })
      })
    },
    sendCancelNotification(emailAddress, mobile, emailMessage, smsMessage) {
      this.sending = true
      const subject = "Vuoroa ei voida vahvista"
      this.$emit('send-cancel-notification', {
        email: emailAddress,
        mobile,
        emailMessage,
        smsMessage,
        subject,
        callback: () => {
          this.sending = false
          this.safeSubmit()
        },
      })
    },
    safeSubmit() {
      let finalChanges = {
        processer: `${this.processer.firstName} ${this.processer.lastName}`,
        date: new Date(),
      }
      const formattedContent = Array.isArray(this.changes.content) ? this.changes.content : this.onObjectToArray(this.changes.content)
      if (this.isBoosted) {
        this.eventLocal.extendedProps.boosted = true
        this.eventLocal.extendedProps.boostedOptions = {
          boostType: this.selectedBoostedOption.value,
          value: this.selectedBoostedOption.value !== 'quick' ? this.selectedBoostedValue : '',
        }
      }
      if(!this.isBoosted && !this.eventLocal.extendedProps.boosted)
      {
        this.eventLocal.extendedProps.boosted = false
        this.eventLocal.extendedProps.boostedOptions = {}
      }
      if (formattedContent.length > 0) {
        if (this.eventLocal.id) {
          finalChanges = {
            ...finalChanges,
            state: this.changes.state,
            content: formattedContent,
          }
        } else {
          finalChanges = {
            ...finalChanges,
            state: 'created',
            content: ['created shift'],
          }
        }
        this.eventLocal.extendedProps.updates.push(finalChanges)
      }
      if (this.assignedTo.length === 0 && this.finalState !== 'draft') this.finalState = 'open'
      if (this.finalState === 'verified') this.finalState = 'booked'
      if (!this.finalState) this.finalState = this.eventLocal.status

      // Create new gigs & update old gigs & update shift
      if (this.isBoosted && !this.assignedTo) {
        this.eventLocal.extendedProps.accountId = this.selectedAccount.value.id
      }

      _.forEach(this.eventLocal.extendedProps.updates, update => {
        if (update.date.seconds) {
          update.date = new Date(update.date.seconds * 1000 + update.date.nanoseconds / 1000000)
        }
      })

      this.onSubmit(
        this.eventLocal,
        this.finalState,
        this.areaId,
        this.contractId,
        this.isBoosted && !this.assignedTo,
      )
      this.$emit('show-hide-sidebar', false)
    },
    onShown() {
      if (this.eventLocal.extendedProps.calendar !== 'unconfirmed' && this.eventLocal.extendedProps.calendar !== 'open' && this.eventLocal.extendedProps.calendar !== 'draft') {
        this.finalState = this.eventLocal.extendedProps.calendar
      }
      if(!this.finalState) this.finalState = this.eventLocal.status
      this.updates = this.event.extendedProps.updates
      this.isOpeningForm = true
      this.$emit('sidebar-open', {
        id: this.event.extendedProps.areaId,
        assignedTo: this.event.extendedProps.assignedTo,
        updates: this.event.extendedProps.updates,
        callback: ({
          location, order, user, accountName, kilometrikorvaus, workDayType, areaId,
        }) => {
          this.location = `${location.address.streetAddress}, ${location.address.city}`
          this.locationDetails = location.locationDetails
          this.sectionInfo = location.sectionInfo
          this.areaName = this.sectionInfo
          this.accountName = accountName
          this.absenceInfo = location.abscenceInfo
          this.contacts = location.contact
          this.assignedUser = {
            ...user,
            id: this.event.extendedProps.assignedTo,
          }
          this.verificationSection = location.sectionInfo
          this.verificationMobile = user?.mobile
          this.emailsTo = order ? order.emails : []
          this.kilometrikorvaus = kilometrikorvaus
          this.workDayType = workDayType
          this.areaId = areaId
          this.contractId = this.event.extendedProps.contractId
          if (user) {
            this.assignedTo = user.fullName
            this.previousAssignedTo = user.fullName
          }
        },
      })
    },
    onHide() {
      this.location = ''
      this.finalState = null
      this.locationDetails = ''
      this.isContractExpired = false
      this.sectionInfo = ''
      this.accountName = ''
      this.verificationSection = ''
      this.verificationHetu = ''
      this.verificationMobile = ''
      this.employeeCancelled = false
      this.absenceInfo = ''
      this.contacts = []
      this.assignedTo = ''
      this.filteredOptions = []
      this.previousAssignedTo = ''
      this.emailsTo = []
      this.assignedUser = null
      this.kilometrikorvaus = 0
      this.workDayType = ''
      this.updates = []
      this.countSetDate = 0
      this.isOpeningForm = false
      this.finalChanges = {}
      this.changes = {
        processer: '',
        content: {},
      }
      this.isLoading = false
      this.isDoubleBooking = false
      this.isBoosted = false
      this.selectedBoostedOption = { label: i18n.t('quick'), value: 'quick' }
      this.selectedBoostedValue = ''
      this.isPartialBlocked = false
      this.areaId = ''
      this.areaName = ''
      this.contractId = null
      this.resetOpenShiftForm()
    },
    onInputChange(text) {
      if (text === '' || text === undefined) {
        this.isLoading = false
        this.isDoubleBooking = false
        this.isPartialBlocked = false
        return
      }
      this.$emit('search-user', {
        q: text,
        callback: result => {
          this.filteredOptions = [{ data: result }]
        },
      })
    },
    onSelected(option) {
      if (option) {
        this.eventLocal.extendedProps.assignedTo = option.item.id
        this.eventLocal.title = `${option.item.firstName}: ${this.sectionInfo}`
        this.changes = {
          state: this.changes.state ? this.changes.state : 'booked',
          content: { ...this.changes.content, booked: option.item.fullName },
        }
        this.assignedUser = option.item
        this.isLoading = true
        this.isDoubleBooking = false
        this.isPartialBlocked = false

        if (option.item.blockedFrom && option.item.blockedFrom.length > 0) {
          if (option.item.blockedFrom.findIndex(i => i.type === 'area' && i.blockId === this.areaId) > -1
          || option.item.blockedFrom.findIndex(i => i.type === 'account' && i.blockId === this.selectedAccount.value.id) > -1
          ) {
            this.isPartialBlocked = true
          }
        }
        this.$emit('fetch-user-upcoming-shifts', {
          assignedTo: option.item.id,
          callback: shifts => {
            this.isLoading = false
            const item = this.eventLocal
            const doubleBookings = shifts.filter(shift => {
              if (shift.id !== item.id && (timeConflict(shift.start, item.start, item.end) || timeConflict(shift.end, item.start, item.end))) {
                return true
                // eslint-disable-next-line max-len
              } if (shift.id !== item.id && (timeConflict(item.start, shift.start, shift.end) || timeConflict(item.end, shift.start, shift.end))) {
                return true
              }
              return false
            })
            this.isDoubleBooking = doubleBookings.length > 0
          },
        })
      }
    },
    getSuggestionValue(option) {
      return option.item.fullName
    },
    onVerifiedToggle(value, emails) {
      if (value === false && this.finalState === 'verified') this.finalState = 'booked'
      const stateName = value === true ? 'verified' : 'unverified'
      this.changes = {
        state: this.changes.state ? this.changes.state : stateName,
        content: emails ? { ...this.changes.content, verifiedStatus: stateName, sendEmailTo: ("'" + emails.join("', '") + "'") } : { ...this.changes.content, verifiedStatus: stateName },
      }
    },
    onUpdateShift(value, propName) {
      this.changes = {
        state: 'updated',
        content: { ...this.changes.content, [propName]: value },
      }

      if (propName === 'workDayType') {
        if (['absence', 'sick', 'sick_document_pending', 'temp_nursing_leave', 'temp_nursing_leave_document_pending'].includes(value)) {
          this.finalState = 'expired'
          return
        }
        if (['work', 'orientation'].includes(value) && this.eventLocal.end > new Date() && this.eventLocal.extendedProps.assignedTo) {
          this.finalState = 'booked'
          return
        }
        this.finalState = null
      }
    },
    onUpdateConfirmed(value) {
      let stateTitle = ''
      if (this.changes.state) {
        stateTitle = this.changes.state
      } else if (value === 'confirmed') {
        stateTitle = 'confirmed'
      } else {
        stateTitle = 'updated'
      }
      this.changes = {
        state: stateTitle,
        content: { ...this.changes.content, status: value },
      }
    },
    onObjectToArray(obj) {
      const array = []
      const updatedArray = []
      Object.keys(obj).forEach(key => {
        if (key === 'verified') {
          array.push(`${obj[key]}: ${true}`)
        } else if (key !== 'updated' && key !== 'verified' && obj[key]) {
            array.push(`${key}: ${obj[key]}`)
        } else if (key === 'updated') {
          Object.keys(key).forEach(subKey => {
            updatedArray.push(`${subKey}: ${key[subKey]}`)
          })
        }
      })
      return array.concat(updatedArray)
    },
    onConvertDate(date) {
      const convertedDate = new Date(date)
      return (`${
        convertedDate.getFullYear().toString().padStart(4, '0')}-${
        (convertedDate.getMonth() + 1).toString().padStart(2, '0')}-${
        convertedDate.getDate().toString().padStart(2, '0')} ${
        convertedDate.getHours().toString().padStart(2, '0')}:${
        convertedDate.getMinutes().toString().padStart(2, '0')}`
      )
    },
    onCancelShift() {
      this.finalState = 'Expired'
      this.changes = {
        state: 'cancelled',
        content: { ...this.changes.content, cancelShift: 'Cancel shift' },
      }
      const name = this.assignedUser ? capitalize(this.assignedUser.firstName) : ''
      const start = this.eventLocal.start?.toLocaleString('fi', {
        day: 'numeric', month: 'numeric', hour: 'numeric', minute: 'numeric',
      })
      const end = this.eventLocal.end?.toLocaleString('fi', { hour: 'numeric', minute: 'numeric' })
      const shiftTitle = this.verificationSection ? capitalize(this.verificationSection) : ''
      const emailMessage = `<p style="text-transform:capitalize">Hei ${name},</p>
                  <p>varaamasi vuoroa <b>` + shiftTitle + ` </b>ajalla <b>` + start + `</b> - <b>` + end + `</b> ei valitettavasti voida vahvistaa, koska kohde on ilmoitti että vuoro on ehtinyt mennä toiselle toimijalle. Pahoittelut tästä.</p>
                  <p>Terveisin Hoiwa - tiimi</p>
              `
      const smsMessage = `Hei ${name},
varaamasi vuoroa ${shiftTitle} ajalla ${start} - ${end} ei valitettavasti voida vahvistaa, koska kohde on ilmoitti että vuoro on ehtinyt mennä toiselle toimijalle. Pahoittelut tästä.
Terveisin Hoiwa-tiimi`
      this.sendCancelNotification(this.formCancel.email, this.formCancel.mobile, emailMessage, smsMessage)
    },
    onShowCancelShift() {
      this.formCancel.email = this.assignedUser.email
      this.formCancel.mobile = this.assignedUser.mobile
      this.newFormData.email = this.assignedUser.email
      this.newFormData.mobile = this.assignedUser.mobile
    },
    onHiddenCancelShift() {
      this.employeeCancelled = false
      this.formCancel.email = ''
      this.formCancel.mobile = ''
      this.newFormData.email = ''
      this.newFormData.mobile = ''
    },
    resetOpenShiftForm() {
      this.newFormData.titleSelected = 'option_1'
      this.newFormData.textAreaFormData = ''
      this.newFormData.email = ''
      this.newFormData.mobile = ''
    },
    openShift() {
      this.assignedTo = ''
      this.eventLocal.extendedProps.assignedTo = ''
      this.eventLocal.title = this.sectionInfo.toLowerCase()
      this.finalState = 'Open'
      this.changes = {
        state: 'cancelled',
        content: {
          ...this.changes.content,
          cancelShift: this.newFormData.titleSelected,
          employeeCancelled: this.employeeCancelled
        },
      }
      let { emailMessage, smsMessage } = this.getMessage
      if (emailMessage.length < 1) {
        emailMessage = toHTMLMessage(this.newFormData.textAreaFormData)
        smsMessage = this.newFormData.textAreaFormData
      }
      this.sendCancelNotification(this.newFormData.email, this.newFormData.mobile, emailMessage, smsMessage)
    },
    onSelectedBoosted(value) {
      this.isBoosted = value
      if (value) {
        this.selectedBoostedOption = { label: i18n.t('quick'), value: 'quick' }
        this.changes = {
          state: this.changes.state ? this.changes.state : 'boosted',
          content: { ...this.changes.content, boosted: this.selectedBoostedOption.value },
        }
      } else {
        const newContent = delete this.changes.content.boosted
        this.changes = {
          state: this.changes.state !== 'boosted' ? this.changes.state : 'updated',
          content: { ...newContent },
        }
      }
    },
    onSelectedBoostType(option) {
      this.changes.content.boosted = option.value
      if (option.value === 'quick') {
        this.selectedBoostedValue = ''
      }
    },
    deleteBoosted() {
      this.eventLocal.extendedProps.boosted = false
      this.eventLocal.extendedProps.boostedOptions = null
      this.changes = {
        state: this.changes.state ? this.changes.state : 'boosted',
        content: { ...this.changes.content, boosted: 'deleted' },
      }
    },
    handlePartialBlock() {
      const user = this.assignedUser
      let blockedFrom = []
      const oldBlocks = user.blockedFrom ? user.blockedFrom : []

      if (this.blockType === 'account') {
        blockedFrom = oldBlocks.concat([{
          type: 'account',
          blockId: this.selectedAccount.value.id,
          blockReason: this.blockReason,
          processer: `${this.processer.firstName} ${this.processer.lastName}`,
          date: new Date(),
        }])
      }

      if (this.blockType === 'area') {
        blockedFrom = oldBlocks.concat([{
          type: 'area',
          blockId: this.areaId,
          blockReason: this.blockReason,
          processer: `${this.processer.firstName} ${this.processer.lastName}`,
          date: new Date(),
        }])
      }
      // Remove element duplicate in array
      blockedFrom = blockedFrom.filter((v, i, a) => a.findIndex(v2 => ['type', 'blockId'].every(k => v2[k] === v[k])) === i)

      this.$store.dispatch('appCalendar/updateUser', { id: user.id, data: { blockedFrom } })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('success'),
              text: 'Työntekijä estetty',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('error'),
              text: this.$t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
      this.$emit('show-hide-sidebar', false)
    },
    resetPartialBlockForm() {
      this.blockType = 'account'
      this.blockId = null
      this.blockReason = ''
    },
  },
  setup(props, { emit }) {
    /*
     ? This is handled quite differently in SFC due to deadlock of `useFormValidation` and this composition function.
     ? If we don't handle it the way it is being handled then either of two composition function used by this SFC get undefined as one of it's argument.
     * The Trick:

     * We created reactive property `clearFormData` and set to null so we can get `resetEventLocal` from `useCalendarEventHandler` composition function.
     * Once we get `resetEventLocal` function which is required by `useFormValidation` we will pass it to `useFormValidation` and in return we will get `clearForm` function which shall be original value of `clearFormData`.
     * Later we just assign `clearForm` to `clearFormData` and can resolve the deadlock. 😎

     ? Behind The Scene
     ? When we passed it to `useCalendarEventHandler` for first time it will be null but right after it we are getting correct value (which is `clearForm`) and assigning that correct value.
     ? As `clearFormData` is reactive it is being changed from `null` to corrent value and thanks to reactivity it is also update in `useCalendarEventHandler` composition function and it is getting correct value in second time and can work w/o any issues.
    */
    const clearFormData = ref(null)

    const {
      eventLocal,
      resetEventLocal,
      calendarOptions,

      // UI
      onSubmit,
      guestsOptions,
    } = useCalendarEventHandler(toRefs(props), clearFormData, emit)

    const {
      refFormObserver,
      getValidationState,
      resetForm,
      clearForm,
    } = formValidation(resetEventLocal, props.clearEventData)

    clearFormData.value = clearForm

    return {
      // Add New Event
      eventLocal,
      calendarOptions,
      onSubmit,
      guestsOptions,

      // Form Validation
      resetForm,
      refFormObserver,
      getValidationState,
      avatarText,
    }
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-autosuggest.scss';
@import '@hw-ui/scss/vue/libs/vue-select.scss';
@import '@hw-ui/scss/vue/libs/vue-flatpicker.scss';
</style>
